'use client'

import { AuthUIProvider } from '@daveyplate/better-auth-ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { YandexMetricaProvider } from 'next-yandex-metrica'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { Toaster } from '~/components/ui/sonner'
import { authClient } from '~/lib/auth.client'

const AUTH_LOCALIZATION = {
  INVALID_USERNAME_OR_PASSWORD: 'Неверное имя пользователя или пароль',
  EMAIL_NOT_VERIFIED: 'Email не подтвержден',
  UNEXPECTED_ERROR: 'Неожиданная ошибка',
  USERNAME_IS_ALREADY_TAKEN: 'Имя пользователя уже занято',
  USERNAME_TOO_SHORT: 'Имя пользователя слишком короткое',
  USERNAME_TOO_LONG: 'Имя пользователя слишком длинное',
  INVALID_USERNAME: 'Неверное имя пользователя',
  OTP_NOT_ENABLED: 'OTP не включен',
  OTP_HAS_EXPIRED: 'OTP истек',
  TOTP_NOT_ENABLED: 'TOTP не включен',
  TWO_FACTOR_NOT_ENABLED: 'Двухфакторная аутентификация не включена',
  BACKUP_CODES_NOT_ENABLED: 'Резервные коды не включены',
  INVALID_BACKUP_CODE: 'Неверный резервный код',
  INVALID_CODE: 'Неверный код',
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: 'Слишком много попыток, запросите новый код',
  INVALID_TWO_FACTOR_COOKIE: 'Неверная кука двухфакторной аутентификации',
  SUBSCRIPTION_NOT_FOUND: 'Подписка не найдена',
  SUBSCRIPTION_PLAN_NOT_FOUND: 'План подписки не найден',
  ALREADY_SUBSCRIBED_PLAN: 'Уже подписан на этот план',
  UNABLE_TO_CREATE_CUSTOMER: 'Не удалось создать клиента',
  FAILED_TO_FETCH_PLANS: 'Не удалось получить планы',
  EMAIL_VERIFICATION_REQUIRED: 'Требуется подтверждение email',
  SUBSCRIPTION_NOT_ACTIVE: 'Подписка не активна',
  SUBSCRIPTION_NOT_SCHEDULED_FOR_CANCELLATION: 'Подписка не запланирована к отмене',
  INVALID_PHONE_NUMBER: 'Неверный номер телефона',
  PHONE_NUMBER_EXIST: 'Номер телефона уже существует',
  INVALID_PHONE_NUMBER_OR_PASSWORD: 'Неверный номер телефона или пароль',
  OTP_NOT_FOUND: 'OTP не найден',
  OTP_EXPIRED: 'OTP истек',
  INVALID_OTP: 'Неверный OTP',
  PHONE_NUMBER_NOT_VERIFIED: 'Номер телефона не подтвержден',
  CHALLENGE_NOT_FOUND: 'Вызов не найден',
  YOU_ARE_NOT_ALLOWED_TO_REGISTER_THIS_PASSKEY: 'Вам не разрешено регистрировать этот ключ доступа',
  FAILED_TO_VERIFY_REGISTRATION: 'Не удалось подтвердить регистрацию',
  PASSKEY_NOT_FOUND: 'Ключ доступа не найден',
  AUTHENTICATION_FAILED: 'Аутентификация не удалась',
  UNABLE_TO_CREATE_SESSION: 'Не удалось создать сессию',
  FAILED_TO_UPDATE_PASSKEY: 'Не удалось обновить ключ доступа',
  YOU_ARE_NOT_ALLOWED_TO_CREATE_A_NEW_ORGANIZATION: 'Вам не разрешено создавать новую организацию',
  YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_ORGANIZATIONS: 'Вы достигли максимального количества организаций',
  ORGANIZATION_ALREADY_EXISTS: 'Организация уже существует',
  ORGANIZATION_NOT_FOUND: 'Организация не найдена',
  USER_IS_NOT_A_MEMBER_OF_THE_ORGANIZATION: 'Пользователь не является членом организации',
  YOU_ARE_NOT_ALLOWED_TO_UPDATE_THIS_ORGANIZATION: 'Вам не разрешено обновлять эту организацию',
  YOU_ARE_NOT_ALLOWED_TO_DELETE_THIS_ORGANIZATION: 'Вам не разрешено удалять эту организацию',
  NO_ACTIVE_ORGANIZATION: 'Нет активной организации',
  USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION: 'Пользователь уже является членом этой организации',
  MEMBER_NOT_FOUND: 'Член не найден',
  ROLE_NOT_FOUND: 'Роль не найдена',
  YOU_ARE_NOT_ALLOWED_TO_CREATE_A_NEW_TEAM: 'Вам не разрешено создавать новую команду',
  TEAM_ALREADY_EXISTS: 'Команда уже существует',
  TEAM_NOT_FOUND: 'Команда не найдена',
  YOU_CANNOT_LEAVE_THE_ORGANIZATION_AS_THE_ONLY_OWNER: 'Вы не можете покинуть организацию как единственный владелец',
  YOU_ARE_NOT_ALLOWED_TO_DELETE_THIS_MEMBER: 'Вам не разрешено удалять этого члена',
  YOU_ARE_NOT_ALLOWED_TO_INVITE_USERS_TO_THIS_ORGANIZATION: 'Вам не разрешено приглашать пользователей в эту организацию',
  USER_IS_ALREADY_INVITED_TO_THIS_ORGANIZATION: 'Пользователь уже приглашен в эту организацию',
  INVITATION_NOT_FOUND: 'Приглашение не найдено',
  YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION: 'Вы не являетесь получателем приглашения',
  YOU_ARE_NOT_ALLOWED_TO_CANCEL_THIS_INVITATION: 'Вам не разрешено отменять это приглашение',
  INVITER_IS_NO_LONGER_A_MEMBER_OF_THE_ORGANIZATION: 'Пригласивший больше не является членом организации',
  YOU_ARE_NOT_ALLOWED_TO_INVITE_USER_WITH_THIS_ROLE: 'Вам не разрешено приглашать пользователя с этой ролью',
  FAILED_TO_RETRIEVE_INVITATION: 'Не удалось получить приглашение',
  YOU_HAVE_REACHED_THE_MAXIMUM_NUMBER_OF_TEAMS: 'Вы достигли максимального количества команд',
  UNABLE_TO_REMOVE_LAST_TEAM: 'Невозможно удалить последнюю команду',
  YOU_ARE_NOT_ALLOWED_TO_UPDATE_THIS_MEMBER: 'Вам не разрешено обновлять этого члена',
  ORGANIZATION_MEMBERSHIP_LIMIT_REACHED: 'Достигнут лимит членства в организации',
  YOU_ARE_NOT_ALLOWED_TO_CREATE_TEAMS_IN_THIS_ORGANIZATION: 'Вам не разрешено создавать команды в этой организации',
  YOU_ARE_NOT_ALLOWED_TO_DELETE_TEAMS_IN_THIS_ORGANIZATION: 'Вам не разрешено удалять команды в этой организации',
  YOU_ARE_NOT_ALLOWED_TO_UPDATE_THIS_TEAM: 'Вам не разрешено обновлять эту команду',
  YOU_ARE_NOT_ALLOWED_TO_DELETE_THIS_TEAM: 'Вам не разрешено удалять эту команду',
  INVITATION_LIMIT_REACHED: 'Достигнут лимит приглашений',
  INVALID_SESSION_TOKEN: 'Неверный токен сессии',
  PASSWORD_COMPROMISED: 'Пароль скомпрометирован',
  INVALID_OAUTH_CONFIGURATION: 'Неверная конфигурация OAuth',
  INVALID_EMAIL: 'Неверный email',
  USER_NOT_FOUND: 'Пользователь не найден',
  TOO_MANY_ATTEMPTS: 'Слишком много попыток',
  MISSING_SECRET_KEY: 'Отсутствует секретный ключ',
  SERVICE_UNAVAILABLE: 'Сервис недоступен',
  VERIFICATION_FAILED: 'Проверка не удалась',
  MISSING_RESPONSE: 'Отсутствует ответ',
  UNKNOWN_ERROR: 'Неизвестная ошибка',
  INVALID_METADATA_TYPE: 'Неверный тип метаданных',
  REFILL_AMOUNT_AND_INTERVAL_REQUIRED: 'Требуется сумма и интервал пополнения',
  REFILL_INTERVAL_AND_AMOUNT_REQUIRED: 'Требуется интервал и сумма пополнения',
  USER_BANNED: 'Пользователь заблокирован',
  UNAUTHORIZED_SESSION: 'Неавторизованная сессия',
  KEY_NOT_FOUND: 'Ключ не найден',
  KEY_DISABLED: 'Ключ отключен',
  KEY_EXPIRED: 'Ключ истек',
  USAGE_EXCEEDED: 'Превышено использование',
  KEY_NOT_RECOVERABLE: 'Ключ не может быть восстановлен',
  EXPIRES_IN_IS_TOO_SMALL: 'Время истечения слишком мало',
  EXPIRES_IN_IS_TOO_LARGE: 'Время истечения слишком велико',
  INVALID_REMAINING: 'Неверное оставшееся значение',
  INVALID_PREFIX_LENGTH: 'Неверная длина префикса',
  INVALID_NAME_LENGTH: 'Неверная длина имени',
  METADATA_DISABLED: 'Метаданные отключены',
  RATE_LIMIT_EXCEEDED: 'Превышен лимит запросов',
  NO_VALUES_TO_UPDATE: 'Нет значений для обновления',
  KEY_DISABLED_EXPIRATION: 'Ключ отключен по истечении срока',
  INVALID_API_KEY: 'Неверный API ключ',
  INVALID_USER_ID_FROM_API_KEY: 'Неверный ID пользователя из API ключа',
  INVALID_API_KEY_GETTER_RETURN_TYPE: 'Неверный тип возвращаемого значения геттера API ключа',
  SERVER_ONLY_PROPERTY: 'Свойство только для сервера',
  FAILED_TO_CREATE_USER: 'Не удалось создать пользователя',
  COULD_NOT_CREATE_SESSION: 'Не удалось создать сессию',
  ANONYMOUS_USERS_CANNOT_SIGN_IN_AGAIN_ANONYMOUSLY: 'Анонимные пользователи не могут войти снова анонимно',
  USER_ALREADY_EXISTS: 'Пользователь уже существует',
  YOU_CANNOT_BAN_YOURSELF: 'Вы не можете заблокировать себя',
  YOU_ARE_NOT_ALLOWED_TO_CHANGE_USERS_ROLE: 'Вам не разрешено изменять роль пользователя',
  YOU_ARE_NOT_ALLOWED_TO_CREATE_USERS: 'Вам не разрешено создавать пользователей',
  YOU_ARE_NOT_ALLOWED_TO_LIST_USERS: 'Вам не разрешено просматривать список пользователей',
  YOU_ARE_NOT_ALLOWED_TO_LIST_USERS_SESSIONS: 'Вам не разрешено просматривать сессии пользователей',
  YOU_ARE_NOT_ALLOWED_TO_BAN_USERS: 'Вам не разрешено блокировать пользователей',
  YOU_ARE_NOT_ALLOWED_TO_IMPERSONATE_USERS: 'Вам не разрешено выдавать себя за пользователей',
  YOU_ARE_NOT_ALLOWED_TO_REVOKE_USERS_SESSIONS: 'Вам не разрешено отзывать сессии пользователей',
  YOU_ARE_NOT_ALLOWED_TO_DELETE_USERS: 'Вам не разрешено удалять пользователей',
  YOU_ARE_NOT_ALLOWED_TO_SET_USERS_PASSWORD: 'Вам не разрешено устанавливать пароль пользователя',
  BANNED_USER: 'Заблокированный пользователь',
  FAILED_TO_CREATE_SESSION: 'Не удалось создать сессию',
  FAILED_TO_UPDATE_USER: 'Не удалось обновить пользователя',
  FAILED_TO_GET_SESSION: 'Не удалось получить сессию',
  INVALID_PASSWORD: 'Неверный пароль',
  INVALID_EMAIL_OR_PASSWORD: 'Неверный email или пароль',
  SOCIAL_ACCOUNT_ALREADY_LINKED: 'Социальный аккаунт уже привязан',
  PROVIDER_NOT_FOUND: 'Провайдер не найден',
  INVALID_TOKEN: 'Неверный токен',
  ID_TOKEN_NOT_SUPPORTED: 'ID токен не поддерживается',
  FAILED_TO_GET_USER_INFO: 'Не удалось получить информацию о пользователе',
  USER_EMAIL_NOT_FOUND: 'Email пользователя не найден',
  PASSWORD_TOO_SHORT: 'Пароль слишком короткий',
  PASSWORD_TOO_LONG: 'Пароль слишком длинный',
  EMAIL_CAN_NOT_BE_UPDATED: 'Email не может быть обновлен',
  CREDENTIAL_ACCOUNT_NOT_FOUND: 'Учетная запись не найдена',
  SESSION_EXPIRED: 'Сессия истекла',
  FAILED_TO_UNLINK_LAST_ACCOUNT: 'Не удалось отвязать последний аккаунт',
  ACCOUNT_NOT_FOUND: 'Аккаунт не найден',
  USER_ALREADY_HAS_PASSWORD: 'У пользователя уже есть пароль',
  APP: 'Приложение',
  ACCOUNT: 'Аккаунт',
  ACCOUNTS: 'Аккаунты',
  ACCOUNTS_DESCRIPTION: 'Управляйте вашими текущими аккаунтами.',
  ACCOUNTS_INSTRUCTIONS: 'Войдите в дополнительный аккаунт.',
  ADD_ACCOUNT: 'Добавить аккаунт',
  ADD_PASSKEY: 'Добавить ключ доступа',
  ALREADY_HAVE_AN_ACCOUNT: 'Уже есть аккаунт?',
  AVATAR: 'Аватар',
  AVATAR_DESCRIPTION: 'Нажмите на аватар, чтобы загрузить свой из файлов.',
  AVATAR_INSTRUCTIONS: 'Аватар необязателен, но настоятельно рекомендуется.',
  BACKUP_CODE_REQUIRED: 'Требуется резервный код',
  BACKUP_CODES: 'Резервные коды',
  BACKUP_CODES_DESCRIPTION: 'Сохраните эти резервные коды в безопасном месте. Вы можете использовать их для доступа к аккаунту, если потеряете способ двухфакторной аутентификации.',
  BACKUP_CODE_PLACEHOLDER: 'Резервный код.',
  BACKUP_CODE: 'Резервный код',
  CANCEL: 'Отмена',
  CHANGE_PASSWORD: 'Изменить пароль',
  CHANGE_PASSWORD_DESCRIPTION: 'Введите текущий пароль и новый пароль.',
  CHANGE_PASSWORD_INSTRUCTIONS: 'Пожалуйста, используйте минимум 8 символов.',
  CHANGE_PASSWORD_SUCCESS: 'Ваш пароль был изменен.',
  CONFIRM_PASSWORD: 'Подтвердите пароль',
  CONFIRM_PASSWORD_PLACEHOLDER: 'Подтвердите пароль',
  CONFIRM_PASSWORD_REQUIRED: 'Требуется подтверждение пароля',
  CONTINUE_WITH_AUTHENTICATOR: 'Продолжить с аутентификатором',
  COPIED_TO_CLIPBOARD: 'Скопировано в буфер обмена',
  COPY_TO_CLIPBOARD: 'Копировать в буфер обмена',
  COPY_ALL_CODES: 'Копировать все коды',
  CONTINUE: 'Продолжить',
  CURRENT_PASSWORD: 'Текущий пароль',
  CURRENT_PASSWORD_PLACEHOLDER: 'Текущий пароль',
  CURRENT_SESSION: 'Текущая сессия',
  DELETE: 'Удалить',
  DELETE_AVATAR: 'Удалить аватар',
  DELETE_ACCOUNT: 'Удалить аккаунт',
  DELETE_ACCOUNT_DESCRIPTION: 'Навсегда удалить ваш аккаунт и все его содержимое. Это действие необратимо, поэтому продолжайте с осторожностью.',
  DELETE_ACCOUNT_INSTRUCTIONS: 'Пожалуйста, подтвердите удаление вашего аккаунта. Это действие необратимо, поэтому продолжайте с осторожностью.',
  DELETE_ACCOUNT_VERIFY: 'Пожалуйста, проверьте вашу почту для подтверждения удаления аккаунта.',
  DELETE_ACCOUNT_SUCCESS: 'Ваш аккаунт был удален.',
  DISABLE_TWO_FACTOR: 'Отключить двухфакторную аутентификацию',
  DISABLED_CREDENTIALS_DESCRIPTION: 'Выберите провайдера для входа в аккаунт',
  DONT_HAVE_AN_ACCOUNT: 'Нет аккаунта?',
  DONE: 'Готово',
  EMAIL: 'Email',
  EMAIL_DESCRIPTION: 'Введите email адрес, который вы хотите использовать для входа.',
  EMAIL_INSTRUCTIONS: 'Пожалуйста, введите действительный email адрес.',
  EMAIL_IS_THE_SAME: 'Email тот же',
  EMAIL_PLACEHOLDER: 'm@example.com',
  EMAIL_REQUIRED: 'Требуется email адрес',
  EMAIL_VERIFY_CHANGE: 'Пожалуйста, проверьте вашу почту для подтверждения изменения.',
  EMAIL_VERIFICATION: 'Пожалуйста, проверьте вашу почту для ссылки подтверждения.',
  ENABLE_TWO_FACTOR: 'Включить двухфакторную аутентификацию',
  IS_INVALID: 'неверен',
  IS_REQUIRED: 'требуется',
  IS_THE_SAME: 'тот же',
  FORGOT_AUTHENTICATOR: 'Забыли аутентификатор?',
  FORGOT_PASSWORD: 'Забыли пароль',
  FORGOT_PASSWORD_ACTION: 'Отправить ссылку для сброса',
  FORGOT_PASSWORD_DESCRIPTION: 'Введите ваш email для сброса пароля',
  FORGOT_PASSWORD_EMAIL: 'Проверьте вашу почту для ссылки сброса пароля.',
  FORGOT_PASSWORD_LINK: 'Забыли пароль?',
  LINK: 'Ссылка',
  MAGIC_LINK: 'Магическая ссылка',
  MAGIC_LINK_ACTION: 'Отправить магическую ссылку',
  MAGIC_LINK_DESCRIPTION: 'Введите ваш email для получения магической ссылки',
  MAGIC_LINK_EMAIL: 'Проверьте вашу почту для магической ссылки',
  EMAIL_OTP: 'Email код',
  EMAIL_OTP_SEND_ACTION: 'Отправить код',
  EMAIL_OTP_VERIFY_ACTION: 'Подтвердить код',
  EMAIL_OTP_DESCRIPTION: 'Введите ваш email для получения кода',
  EMAIL_OTP_VERIFICATION_SENT: 'Пожалуйста, проверьте вашу почту для кода подтверждения.',
  NAME: 'Имя',
  NAME_DESCRIPTION: 'Пожалуйста, введите ваше полное имя или отображаемое имя.',
  NAME_INSTRUCTIONS: 'Пожалуйста, используйте максимум 32 символа.',
  NAME_PLACEHOLDER: 'Имя',
  NEW_PASSWORD: 'Новый пароль',
  NEW_PASSWORD_PLACEHOLDER: 'Новый пароль',
  NEW_PASSWORD_REQUIRED: 'Требуется новый пароль',
  ONE_TIME_PASSWORD: 'Одноразовый пароль',
  OR_CONTINUE_WITH: 'Или продолжить с',
  PASSKEY: 'Ключ доступа',
  PASSKEYS: 'Ключи доступа',
  PASSKEYS_DESCRIPTION: 'Управляйте вашими ключами доступа для безопасного доступа.',
  PASSKEYS_INSTRUCTIONS: 'Безопасно получайте доступ к аккаунту без пароля.',
  PERSONAL_ACCOUNT: 'Личный аккаунт',
  API_KEYS: 'API ключи',
  API_KEYS_DESCRIPTION: 'Управляйте вашими API ключами для безопасного доступа.',
  API_KEYS_INSTRUCTIONS: 'Создавайте API ключи для программного доступа к аккаунту.',
  CREATE_API_KEY: 'Создать API ключ',
  CREATE_API_KEY_DESCRIPTION: 'Введите уникальное имя для вашего API ключа, чтобы отличать его от других ключей.',
  API_KEY_NAME_PLACEHOLDER: 'Новый API ключ',
  API_KEY_CREATED: 'API ключ создан',
  CREATE_API_KEY_SUCCESS: 'Пожалуйста, скопируйте ваш API ключ и сохраните его в безопасном месте. По соображениям безопасности мы не сможем показать его снова.',
  NEVER_EXPIRES: 'Никогда не истекает',
  EXPIRES: 'Истекает',
  NO_EXPIRATION: 'Без срока действия',
  CREATE_ORGANIZATION: 'Создать организацию',
  ORGANIZATION: 'Организация',
  ORGANIZATION_NAME: 'Название',
  ORGANIZATION_NAME_PLACEHOLDER: 'Acme Inc.',
  ORGANIZATION_NAME_DESCRIPTION: 'Это видимое название вашей организации.',
  ORGANIZATION_NAME_INSTRUCTIONS: 'Пожалуйста, используйте максимум 32 символа.',
  ORGANIZATION_SLUG: 'URL слаг',
  ORGANIZATION_SLUG_DESCRIPTION: 'Это URL пространство имен вашей организации.',
  ORGANIZATION_SLUG_INSTRUCTIONS: 'Пожалуйста, используйте максимум 48 символов.',
  ORGANIZATION_SLUG_PLACEHOLDER: 'acme-inc',
  CREATE_ORGANIZATION_SUCCESS: 'Организация успешно создана',
  PASSWORD: 'Пароль',
  PASSWORD_PLACEHOLDER: 'Пароль',
  PASSWORD_REQUIRED: 'Требуется пароль',
  PASSWORDS_DO_NOT_MATCH: 'Пароли не совпадают',
  PROVIDERS: 'Провайдеры',
  PROVIDERS_DESCRIPTION: 'Подключите ваш аккаунт к стороннему сервису.',
  RECOVER_ACCOUNT: 'Восстановить аккаунт',
  RECOVER_ACCOUNT_ACTION: 'Восстановить аккаунт',
  RECOVER_ACCOUNT_DESCRIPTION: 'Пожалуйста, введите резервный код для доступа к аккаунту',
  REMEMBER_ME: 'Запомнить меня',
  RESEND_CODE: 'Отправить код повторно',
  RESEND_VERIFICATION_EMAIL: 'Отправить письмо подтверждения повторно',
  RESET_PASSWORD: 'Сбросить пароль',
  RESET_PASSWORD_ACTION: 'Сохранить новый пароль',
  RESET_PASSWORD_DESCRIPTION: 'Введите ваш новый пароль ниже',
  RESET_PASSWORD_SUCCESS: 'Пароль успешно сброшен',
  REQUEST_FAILED: 'Запрос не выполнен',
  REVOKE: 'Отозвать',
  DELETE_API_KEY: 'Удалить API ключ',
  DELETE_API_KEY_CONFIRM: 'Вы уверены, что хотите удалить этот API ключ?',
  API_KEY: 'API ключ',
  SIGN_IN: 'Войти',
  SIGN_IN_ACTION: 'Вход',
  SIGN_IN_DESCRIPTION: 'Введите ваш email ниже для входа в аккаунт',
  SIGN_IN_USERNAME_DESCRIPTION: 'Введите ваше имя пользователя или email ниже для входа в аккаунт',
  SIGN_IN_WITH: 'Войти с',
  SIGN_OUT: 'Выйти',
  SIGN_UP: 'Регистрация',
  SIGN_UP_ACTION: 'Создать аккаунт',
  SIGN_UP_DESCRIPTION: 'Введите вашу информацию для создания аккаунта',
  SIGN_UP_EMAIL: 'Проверьте вашу почту для ссылки подтверждения.',
  SESSIONS: 'Сессии',
  SESSIONS_DESCRIPTION: 'Управляйте вашими активными сессиями и отзывайте доступ.',
  SET_PASSWORD: 'Установить пароль',
  SET_PASSWORD_DESCRIPTION: 'Нажмите кнопку ниже, чтобы получить email для настройки пароля вашего аккаунта.',
  SETTINGS: 'Настройки',
  SAVE: 'Сохранить',
  SECURITY: 'Безопасность',
  SWITCH_ACCOUNT: 'Переключить аккаунт',
  TRUST_DEVICE: 'Доверять этому устройству',
  TWO_FACTOR: 'Двухфакторная аутентификация',
  TWO_FACTOR_ACTION: 'Подтвердить код',
  TWO_FACTOR_DESCRIPTION: 'Пожалуйста, введите ваш одноразовый пароль для продолжения',
  TWO_FACTOR_CARD_DESCRIPTION: 'Добавьте дополнительный уровень безопасности к вашему аккаунту.',
  TWO_FACTOR_DISABLE_INSTRUCTIONS: 'Пожалуйста, введите ваш пароль для отключения 2FA.',
  TWO_FACTOR_ENABLE_INSTRUCTIONS: 'Пожалуйста, введите ваш пароль для включения 2FA',
  TWO_FACTOR_ENABLED: 'Двухфакторная аутентификация была включена',
  TWO_FACTOR_DISABLED: 'Двухфакторная аутентификация была отключена',
  TWO_FACTOR_PROMPT: 'Двухфакторная аутентификация',
  TWO_FACTOR_TOTP_LABEL: 'Отсканируйте QR код с помощью вашего аутентификатора',
  SEND_VERIFICATION_CODE: 'Отправить код подтверждения',
  UNLINK: 'Отвязать',
  UPDATED_SUCCESSFULLY: 'Успешно обновлено',
  USERNAME: 'Имя пользователя',
  USERNAME_DESCRIPTION: 'Введите имя пользователя, которое вы хотите использовать для входа.',
  USERNAME_INSTRUCTIONS: 'Пожалуйста, используйте максимум 32 символа.',
  USERNAME_PLACEHOLDER: 'Имя пользователя',
  SIGN_IN_USERNAME_PLACEHOLDER: 'Имя пользователя или email',
  VERIFY_YOUR_EMAIL: 'Подтвердите ваш email',
  VERIFY_YOUR_EMAIL_DESCRIPTION: 'Пожалуйста, подтвердите ваш email адрес. Проверьте вашу почту для письма подтверждения. Если вы не получили письмо, нажмите кнопку ниже, чтобы отправить повторно.',
  GO_BACK: 'Вернуться назад',
  SESSION_NOT_FRESH: 'Ваша сессия не свежая. Пожалуйста, войдите снова.',
  UPLOAD_AVATAR: 'Загрузить аватар',
  LOGO: 'Логотип',
  LOGO_DESCRIPTION: 'Нажмите на логотип, чтобы загрузить свой из файлов.',
  LOGO_INSTRUCTIONS: 'Логотип необязателен, но настоятельно рекомендуется.',
  UPLOAD: 'Загрузить',
  UPLOAD_LOGO: 'Загрузить логотип',
  DELETE_LOGO: 'Удалить логотип',
  PRIVACY_POLICY: 'Политика конфиденциальности',
  TERMS_OF_SERVICE: 'Условия использования',
  PROTECTED_BY_RECAPTCHA: 'Этот сайт защищен reCAPTCHA.',
  BY_CONTINUING_YOU_AGREE: 'Продолжая, вы соглашаетесь с',
  USER: 'Пользователь',
  ORGANIZATIONS: 'Организации',
  ORGANIZATIONS_DESCRIPTION: 'Управляйте вашими организациями и членствами.',
  ORGANIZATIONS_INSTRUCTIONS: 'Создайте организацию для сотрудничества с другими пользователями.',
  LEAVE_ORGANIZATION: 'Покинуть организацию',
  LEAVE_ORGANIZATION_CONFIRM: 'Вы уверены, что хотите покинуть эту организацию?',
  LEAVE_ORGANIZATION_SUCCESS: 'Вы успешно покинули организацию.',
  MANAGE_ORGANIZATION: 'Управлять организацией',
  REMOVE_MEMBER: 'Удалить члена',
  REMOVE_MEMBER_CONFIRM: 'Вы уверены, что хотите удалить этого члена из организации?',
  REMOVE_MEMBER_SUCCESS: 'Член успешно удален',
  INVITE_MEMBER: 'Пригласить члена',
  MEMBERS: 'Члены',
  MEMBERS_DESCRIPTION: 'Добавляйте или удаляйте членов и управляйте их ролями.',
  MEMBERS_INSTRUCTIONS: 'Пригласите новых членов в вашу организацию.',
  INVITE_MEMBER_DESCRIPTION: 'Отправьте приглашение для добавления нового члена в вашу организацию.',
  ROLE: 'Роль',
  SELECT_ROLE: 'Выберите роль',
  ADMIN: 'Администратор',
  MEMBER: 'Член',
  GUEST: 'Гость',
  OWNER: 'Владелец',
  UPDATE_ROLE_DESCRIPTION: 'Обновить роль для этого члена',
  UPDATE_ROLE: 'Обновить роль',
  MEMBER_ROLE_UPDATED: 'Роль члена успешно обновлена',
  SEND_INVITATION: 'Отправить приглашение',
  SEND_INVITATION_SUCCESS: 'Приглашение успешно отправлено',
  PENDING_INVITATIONS: 'Ожидающие приглашения',
  PENDING_INVITATIONS_DESCRIPTION: 'Управляйте ожидающими приглашениями в вашу организацию.',
  PENDING_USER_INVITATIONS_DESCRIPTION: 'Приглашения, которые вы получили от организаций.',
  CANCEL_INVITATION: 'Отменить приглашение',
  INVITATION_CANCELLED: 'Приглашение успешно отменено',
  ACCEPT_INVITATION: 'Принять приглашение',
  ACCEPT_INVITATION_DESCRIPTION: 'Вас пригласили присоединиться к организации.',
  INVITATION_ACCEPTED: 'Приглашение успешно принято',
  INVITATION_REJECTED: 'Приглашение успешно отклонено',
  ACCEPT: 'Принять',
  REJECT: 'Отклонить',
  INVITATION_EXPIRED: 'Это приглашение истекло',
  DELETE_ORGANIZATION: 'Удалить организацию',
  DELETE_ORGANIZATION_DESCRIPTION: 'Навсегда удалить вашу организацию и все ее содержимое. Это действие необратимо — пожалуйста, продолжайте с осторожностью.',
  DELETE_ORGANIZATION_SUCCESS: 'Организация успешно удалена',
  DELETE_ORGANIZATION_INSTRUCTIONS: 'Введите слаг организации для продолжения:',
  SLUG_REQUIRED: 'Требуется слаг организации',
  SLUG_DOES_NOT_MATCH: 'Слаг не совпадает',
  UNKNOWN: 'Неизвестно',
}

export function ClientProviders(props: { children: React.ReactNode }) {
  const router = useRouter()
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
          },
        },
      }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <AuthUIProvider
        authClient={authClient}
        navigate={router.push}
        replace={router.replace}
        onSessionChange={() => {
          // Очистка кеша роутера (для защищенных маршрутов)
          router.refresh()
        }}
        account={true}
        Link={Link}
        localization={AUTH_LOCALIZATION}
      >
        <YandexMetricaProvider
          tagID={97419545}
          initParameters={{
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
            webvisor: true,
          }}
          router="app"
        >
          {props.children}
        </YandexMetricaProvider>
      </AuthUIProvider>

      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster />
    </QueryClientProvider>
  )
}
